import { faEye, faEyeSlash, faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, IconButton, InputAdornment, MenuItem, styled, Table, TableBody, TableCell, TableRow, TextField, TextFieldProps, Typography } from '@mui/material';
import { grey } from "@mui/material/colors";
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useParams } from "react-router-dom";
import { SendFeedback } from "../api/dtos/dtos";
import Center from "../components/common/Center";
import Hr from "../components/common/HorizontalRule";
import { UserFormatError } from "../constants/exceptions/CustomExceptions";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "../constants/styles/AlertConstants";
import AlertContext from "../contexts/styles/AlertContext";
import useServiceStackClient from "../hooks/api/useServiceStackClient";
import { SpeciesContext } from '../contexts/data/SpeciesContext';

interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];
    
    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ? 
                    `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                disabled ?
                    'rgba(0, 0, 0, 0.2)' :
                    'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const Container = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel, 
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '350px', maxWidth: '350px' }}>
                {primaryLabel}
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ? 
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            disableRipple
                                            sx={{ width: '24px', height: '24px' }}    
                                            onClick={() => setShowPassword(prev => !prev)}
                                        >
                                            {
                                                showPassword ? 
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> : 
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                disabled ? (
                                    <InputAdornment position="end">
                                        <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }}/>
                                    </InputAdornment>
                                ) : 
                                null
                            }}
                            autoComplete='password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const FeedbackPage: FC = () => {

    //
    // -- Refs --
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    //
    // -- Hooks --
    const client = useServiceStackClient();

    // -- Context --
    const { addAlert } = useContext(AlertContext);
    const { species } = useContext(SpeciesContext)
    const { species: speciesName } = useParams();

    // -- States --
    const [feedbackDetails, setFeedbackDetails] = useState<SendFeedback>();
    const [feedbackDetailsState, setFeedbackDetailsState] = useState<Record<string, FieldState>>({});
    const [error, setError] = useState<boolean>(false);

    // Select options
    const ratingOptions = useMemo(() => {
        return [
            <MenuItem key={"excellent"} value="Excellent">Excellent</MenuItem>,
            <MenuItem key={"good"} value="Good">Good</MenuItem>,
            <MenuItem key={"average"} value="Average">Average</MenuItem>,
            <MenuItem key={"poor"} value="Poor">Poor</MenuItem>,
            <MenuItem key={"verypoor"} value="Very Poor">Very Poor</MenuItem>
        ];
    }, []);

    const easilyUnderstoodOptions = useMemo(() => {
        return [
            <MenuItem key={"yes"} value="Yes">Yes</MenuItem>,
            <MenuItem key={"no"} value="No">No</MenuItem>
        ];
    }, []);

    const relevantQuestionOptions = useMemo(() => {
        return [
            <MenuItem key={"yes"} value="Yes">Yes</MenuItem>,
            <MenuItem key={"no"} value="No">No</MenuItem>,
            <MenuItem key={"undecided"} value="Undecided">Undecided</MenuItem>
        ];
    }, []);

    const preparedToRevceiveEmailOptions = useMemo(() => {
        return [
            <MenuItem key={"yes"} value="Yes">Yes</MenuItem>,
            <MenuItem key={"no"} value="No">No</MenuItem>,
            <MenuItem key={"undecided"} value="Undecided">Undecided</MenuItem>
        ];
    }, []);

    const handleFeedbackChange = useCallback((field: string, value: any) => {

        setFeedbackDetails(c => {
            return new SendFeedback({
                ...c,
                [field]: value
            });
        })
    }, [setFeedbackDetails]);

    const handleFeedbackSubmit = useCallback(async () => {
        let valid = false;

        if (!valid && feedbackDetails?.rating) {valid = true};
        if (!valid && feedbackDetails?.easilyUnderstood) {valid = true};
        if (!valid && feedbackDetails?.relevantQuestions) {valid = true};
        if (!valid && feedbackDetails?.difficulties) {valid = true};
        if (!valid && feedbackDetails?.modifications) {valid = true};
        if (!valid && feedbackDetails?.preparedToRevceiveEmails) {valid = true};
        if (!valid && feedbackDetails?.suggestions) {valid = true};
        if (!valid && feedbackDetails?.email) { valid = true };

        if (recaptchaRef?.current?.getValue() === "") {
            recaptchaRef.current?.reset();
            valid = false;
        }

        if (valid) {
            try {
                // Make feedback request
                await client.post(
                    new SendFeedback({
                        rating: feedbackDetails?.rating,
                        easilyUnderstood: feedbackDetails?.easilyUnderstood,
                        relevantQuestions: feedbackDetails?.relevantQuestions,
                        difficulties: feedbackDetails?.difficulties,
                        modifications: feedbackDetails?.modifications,
                        preparedToRevceiveEmails: feedbackDetails?.preparedToRevceiveEmails,
                        suggestions: feedbackDetails?.suggestions,
                        email: feedbackDetails?.email,
                        species: species.species
                    })
                );
                
                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                            Feedback sent successfully.
                        </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });
                

            } catch (error: any) {

                recaptchaRef.current?.reset();

                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                    {newState[Object.keys(newState)[0]].message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setFeedbackDetailsState(newState);
                    }
                    else {

                        recaptchaRef.current?.reset();

                        addAlert({
                            content: <AlertTitle>
                                    {error.responseStatus.message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {

                    recaptchaRef.current?.reset();

                    addAlert({
                        content: <AlertTitle>
                                {error.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }
        else {
            setError(true);
        }

    }, [client, feedbackDetails, addAlert, setFeedbackDetailsState, setError]);
    
    return (
        <Container>
            <Typography color="#333" variant='h2'>Feedback</Typography>
            <Hr color={grey[400]} sx={{ margin: '40px 0' }} />
            
            <Typography variant='h3' sx={{fontWeight: 'bold'}}>Landholder and community feedback welcome.</Typography>
            <br />
            <Typography>The Project Team are very interested in any ideas you may have regarding how this website and the mapping tool can be improved to be more useful to landholders, community groups and anyone with a {speciesName?.replace("scan", "")?.replace("alert", "").toLowerCase()} issue. Please include any ideas or suggestions you might have in the text-box below and include your email address if you are happy for us to contact you about this feedback. Thank you.</Typography>
            <br />

            <Table>
                <TableBody>
                    <StyledTableRow
                        select
                        items={ratingOptions}
                        primaryLabel="How did you rate this website?"
                        fieldState={feedbackDetailsState['rating']}
                        value={feedbackDetailsState!.rating}
                        onChange={(v) => handleFeedbackChange('rating', v.target.value)}
                    />
                    <StyledTableRow
                        select
                        items={easilyUnderstoodOptions}
                        primaryLabel="Was the content of the website easily understood?"
                        fieldState={feedbackDetailsState['easilyUnderstood']}
                        value={feedbackDetailsState!.easilyUnderstood}
                        onChange={(v) => handleFeedbackChange('easilyUnderstood', v.target.value)}
                    />
                    <StyledTableRow
                        select
                        items={relevantQuestionOptions}
                        primaryLabel="Were the questions relevant to your circumstances?"
                        fieldState={feedbackDetailsState['relevantQuestions']}
                        value={feedbackDetailsState!.relevantQuestions}
                        onChange={(v) => handleFeedbackChange('relevantQuestions', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel={`Did you experience any difficulties using ${speciesName?.capitaliseFirstLetter()?.replace("scan", "Scan")?.replace("alert", "Alert")}?`}
                        fieldState={feedbackDetailsState['difficulties']}
                        value={feedbackDetails?.difficulties}
                        onChange={(v) => handleFeedbackChange('difficulties', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Would you modify any of the questions? If so, please specify."
                        fieldState={feedbackDetailsState['modifications']}
                        value={feedbackDetails?.modifications}
                        onChange={(v) => handleFeedbackChange('modifications', v.target.value)}
                    />
                    <StyledTableRow
                        select
                        items={preparedToRevceiveEmailOptions}
                        primaryLabel={`Would you be prepared to receive 3-4 emails during the year about ${speciesName?.capitaliseFirstLetter()?.replace("scan", "Scan")?.replace("alert", "Alert")}?`}
                        fieldState={feedbackDetailsState['preparedToRevceiveEmails']}
                        value={feedbackDetailsState!.preparedToRevceiveEmails}
                        onChange={(v) => handleFeedbackChange('preparedToRevceiveEmails', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Do you have any further suggestions or feedback?"
                        fieldState={feedbackDetailsState['suggestions']}
                        value={feedbackDetails?.suggestions}
                        onChange={(v) => handleFeedbackChange('suggestions', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Email Address (optional):"
                        fieldState={feedbackDetailsState['email']}
                        value={feedbackDetails?.email}
                        onChange={(v) => handleFeedbackChange('email', v.target.value)}
                    />
                </TableBody>
            </Table>

            <Box
                sx={{
                    pt: '5px',
                    pl: '358px'
                }}
            >
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LebIOUjAAAAAJBe-lms4_lrqq6yl2wU8wX8ZDTo"
                    onChange={() => {null}}
                />
            </Box>
            <Box
                sx={{
                    pt: '5px',
                    pl: '358px',
                    display: error ? 'block' : 'none'
                }}
            >
                <Typography sx={{color:'red'}}>Please select an option on this form or write a comment before submitting feedback.</Typography>
            </Box>

            <Center ha='end' m='15px 10px'>
                <StyledConfirmButton
                    onClick={handleFeedbackSubmit}
                >
                    <Typography fontWeight='600'>Submit</Typography>
                </StyledConfirmButton>
            </Center>
        
        </Container>
    )
}
export default FeedbackPage
